import { useEffect, useRef } from 'react';

type UseRecentChartItemSelectionOptions = {
  resetAfterMs?: number;
};

type UseRecentChartItemSelectionResult = {
  markRecentItemSelection: (dataIndex: number) => void;
  shouldSuppressAxisSelection: (dataIndex: number) => boolean;
};

const useRecentChartItemSelection = ({
  resetAfterMs = 600,
}: UseRecentChartItemSelectionOptions = {}): UseRecentChartItemSelectionResult => {
  const lastItemClickRef = useRef<{ dataIndex: number; timestamp: number } | null>(null);
  const lastItemClickTimeoutRef = useRef<number | null>(null);

  const markRecentItemSelection = (dataIndex: number): void => {
    lastItemClickRef.current = { dataIndex, timestamp: Date.now() };
    if (lastItemClickTimeoutRef.current !== null) {
      window.clearTimeout(lastItemClickTimeoutRef.current);
    }
    lastItemClickTimeoutRef.current = window.setTimeout(() => {
      lastItemClickRef.current = null;
      lastItemClickTimeoutRef.current = null;
    }, resetAfterMs);
  };

  const shouldSuppressAxisSelection = (dataIndex: number): boolean => {
    const lastItemClick = lastItemClickRef.current;
    return (
      lastItemClick?.dataIndex === dataIndex && Date.now() - lastItemClick.timestamp < resetAfterMs
    );
  };

  useEffect(() => {
    return () => {
      if (lastItemClickTimeoutRef.current !== null) {
        window.clearTimeout(lastItemClickTimeoutRef.current);
        lastItemClickTimeoutRef.current = null;
      }
    };
  }, []);

  return {
    markRecentItemSelection,
    shouldSuppressAxisSelection,
  };
};

export { useRecentChartItemSelection };
